import { useEffect, useState, useRef, useContext } from "react";
import { Toast } from "primereact/toast";
import HeaderWithBackButton from "../common/HeaderWIthBackButton";
import InventoryTable from "./InventoryTable";
import InventoryService from "../../services/InventoryService";
import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { useFormik } from "formik";
import { contextStore } from "../../context/CustomStateProvider";
import InventoryUploadValidationSchema from "../../validation/InventoryUploadValidation";
import axios from "axios";

export type InventoryItem = {
  inventoryId: number;
  inventoryName: string;
  description: string;
  uploadedDate: string;
  expiryDate: string;
  fileUrl: string | null;
  userId: number;
};

const ALLOWED_INVENTORY_EXTENSIONS = [".pdf", ".xls", ".xlsx"];

const InventoryLayout: React.FC = () => {
  const context = useContext(contextStore);
  const resource = context.state.strings;
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [loadingSpinnerLayout, setLoadingSpinnerLayout] =
    useState<boolean>(true);
  const [reloadInventory, setReloadInventory] = useState<boolean>(false);
  const toast = useRef<Toast>(null);
  const [progressForInventory, setProgressForInventory] = useState<number>(0);
  const [inventoryFileName, setInventoryFileName] = useState<string>("");
  const [uploadVisible, setUploadVisible] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const fetchInventoryItems = async () => {
    try {
      const res = await InventoryService.GetAllInventoryList();
      if (res && Array.isArray((res as any).data)) {
        setInventoryItems((res as any).data);
      } else if (Array.isArray(res)) {
        setInventoryItems(res);
      } else {
        setInventoryItems([]);
      }
    } catch (error) {
      console.error("Error fetching inventory list:", error);
    } finally {
      setTimeout(() => setLoadingSpinnerLayout(false), 400);
    }
  };

  useEffect(() => {
    fetchInventoryItems();
  }, [reloadInventory]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const filteredItems = inventoryItems.filter((item) =>
    (item?.inventoryName ?? "")
      .toLowerCase()
      .includes((searchQuery ?? "").toLowerCase()),
  );

  const onInventoryFileSelect = (event: any) => {
    const file: File = event.files[0];
    if (!file) {
      toast.current?.show({
        severity: "warn",
        summary: "Warning",
        detail: "Something went wrong.",
        life: 3000,
      });
      return;
    }

    const fileExtension = file.name
      .slice(file.name.lastIndexOf("."))
      .toLowerCase();
    if (!ALLOWED_INVENTORY_EXTENSIONS.includes(fileExtension)) {
      toast.current?.show({
        severity: "warn",
        summary: "Warning",
        detail: "Please select a PDF or Excel (.xls, .xlsx) file only.",
        life: 3000,
      });
      return;
    }

    setSelectedFile(file);
    setInventoryFileName(file.name);
    formik.setFieldValue("inventoryUpload", file.name);
  };

  const handleSubmit = async (values: any) => {
    if (!selectedFile) {
      toast.current?.show({
        severity: "warn",
        summary: "Warning",
        detail: "Please select a document before submitting.",
        life: 3000,
      });
      return;
    }

    UploadInventoryFile({
      File: selectedFile,
      InventoryName: values.inventoryname,
      Description: values.description,
    });
  };

  const initialValues = {
    inventoryname: "",
    description: "",
    inventoryUpload: "",
  };

  const formik = useFormik({
    initialValues,
    validationSchema: InventoryUploadValidationSchema,
    onSubmit: handleSubmit,
  });

  const UploadInventoryFile = (data: any) => {
    InventoryService.UploadInventory(data, (progress: number) =>
      setProgressForInventory(progress),
    )
      .then(async (res: any) => {
        const responseData = res?.data;

        if (responseData?.p_response === false) {
          toast.current?.show({
            severity: "error",
            summary: "Upload Failed",
            detail: responseData?.p_msg || "Failed to upload inventory data.",
            life: 4000,
          });
          setTimeout(() => setProgressForInventory(0), 500);
          return;
        }

        const uploadedInventoryId = responseData?.p_inventoryid;
        const presignedURL = responseData?.presigned_url;

        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail:
            responseData?.p_msg ||
            "The inventory document has been successfully uploaded.",
          life: 3000,
        });

        setReloadInventory((prev) => !prev);

        if (uploadedInventoryId) {
          await simulateEmbeddingProgress(async () => {
            await callProcessInventoryEmbedding(
              data.File,
              uploadedInventoryId,
              presignedURL,
            );
          });
          hideUploadDialog();
        }

        setTimeout(() => setProgressForInventory(0), 2000);
      })
      .catch((error) => {
        console.error("Inventory upload failed:", error);
        toast.current?.show({
          severity: "error",
          summary: "Upload Failed",
          detail: "Failed to upload inventory document.",
          life: 4000,
        });
        setTimeout(() => setProgressForInventory(0), 500);
      });
  };

  const simulateEmbeddingProgress = async (callback: Function) => {
    setProgressForInventory(60); // Start embedding from 60%

    const interval = setInterval(() => {
      setProgressForInventory((prev) => {
        if (prev >= 95) {
          clearInterval(interval);
          return prev;
        }
        return prev + 2; // Smooth incremental increase
      });
    }, 300);

    try {
      await callback(); // Actually call process_inventory
    } finally {
      clearInterval(interval);
      setProgressForInventory(100);
    }
  };

  const callProcessInventoryEmbedding = async (
    file: File,
    uploadInventoryId: number,
    presignedURL: string,
  ) => {
    try {
      const formData = new FormData();
      formData.append("inventory_file", file);
      formData.append("upload_inventory_id", uploadInventoryId.toString());
      formData.append("presigned_url", presignedURL);

      const API_BASE_URL =
        process.env.REACT_APP_API_BASE_URL ||
        // "https://cc4jaxi5vi.execute-api.ap-south-1.amazonaws.com/Prod";
        "https://chataiapi-dev.vedbhoomi.co.in";
      // "http://127.0.0.1:8000";
      const response = await axios.post(
        `${API_BASE_URL}/process_inventory`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      if (response.status === 200) {
        toast.current?.show({
          severity: "success",
          summary: "Embedding Started",
          detail: `Embedding initiated for ${file.name}`,
          life: 3000,
        });
      }
    } catch (error: any) {
      console.error("Error during inventory embedding:", error);
      toast.current?.show({
        severity: "error",
        summary: "Embedding Failed",
        detail: "Unable to process embedding for this inventory document.",
        life: 4000,
      });
    }
  };

  const hideUploadDialog = () => {
    setUploadVisible(false);
    formik.resetForm();
    setSelectedFile(null);
    setInventoryFileName("");
    setProgressForInventory(0);
  };
  const onUploadInventoryClick = () => {
    const hasActiveInventory = inventoryItems.some(
      (item) =>
        item.expiryDate && new Date(item.expiryDate).getTime() >= Date.now(),
    );

    if (hasActiveInventory) {
      toast.current?.show({
        severity: "warn",
        summary: "Warning",
        detail: "You still have an active inventory.",
        life: 3000,
      });
    }

    setUploadVisible(true);
  };

  const onBackButtonClick = () => {
    toast.current?.show({
      severity: "info",
      summary: "Back",
      detail: "Returning to previous page.",
      life: 2000,
    });
  };

  const cardControls = [
    {
      ctrlType: "button",
      name: "btnUploadInventory",
      title: "Upload Inventory data",
      type: "button",
      disabled: false,
      className: "green-btn mr-2",
    },
  ];

  const onButtonClick = (e: string) => {
    if (e === "btnUploadInventory") {
      onUploadInventoryClick();
    }
  };

  return (
    <div className="ml-2">
      <Toast ref={toast} />

      <HeaderWithBackButton
        title={resource.inventoryTitle || "Inventory Settings"}
        controls={cardControls}
        onButtonClicked={onButtonClick}
        onBackButtonClick={onBackButtonClick}
        handleSearch={handleSearch}
        searchQuery={searchQuery}
      />

      <Dialog
        className="popup popup-card-layout"
        visible={uploadVisible}
        draggable={false}
        closable={progressForInventory === 0}
        style={{ width: "60vw" }}
        onHide={hideUploadDialog}
        header={
          <div className="flex justify-content-between align-items-center w-full">
            <span>{resource.uploadInventoryDialogHeader || "Inventory"}</span>
            <i
              className="pi pi-times cursor-pointer text-gray-600 hover:text-gray-900"
              onClick={hideUploadDialog}
              style={{
                fontSize: "1.2rem",
                marginRight: "0.5rem",
              }}
            ></i>
          </div>
        }
      >
        <form onSubmit={formik.handleSubmit}>
          <div className="card-wrapper">
            <div className="flex mb-2">
              <div className="col-4 relative">
                <label
                  htmlFor="inventoryname"
                  className="block text-900 font-medium mb-2"
                >
                  {resource.inventoryname || "Inventory Name"}
                </label>
                <InputText
                  id="inventoryname"
                  type="text"
                  placeholder="Enter name"
                  className="w-full mb-2"
                  value={formik.values.inventoryname}
                  onChange={formik.handleChange}
                  tabIndex={1}
                  ref={inputRef}
                />
              </div>
              <div className="col-4 relative">
                <label
                  htmlFor="description"
                  className="block text-900 font-medium mb-2"
                >
                  {resource.inventoryDescription || "Description"}
                </label>
                <InputTextarea
                  id="description"
                  placeholder="Enter description"
                  className="w-full mb-2"
                  rows={3}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  tabIndex={2}
                />
              </div>
            </div>
            <div className="flex mb-2">
              <div className="col-4 relative">
                <label
                  htmlFor="inventoryUpload"
                  className="block text-900 font-medium mb-2"
                >
                  {resource.inventoryUpload || "Document"}{" "}
                  <span className="text-red">*</span>
                </label>
                <div className="flex">
                  <InputText
                    id="inventoryUpload"
                    type="text"
                    placeholder="Select document"
                    className="fileinput ml-1"
                    value={inventoryFileName}
                    onChange={formik.handleChange}
                    tabIndex={3}
                  />
                  <FileUpload
                    mode="basic"
                    accept=".pdf,.xls,.xlsx"
                    auto
                    customUpload
                    chooseLabel="Select"
                    className="flex justify-content-center fileselectbtn"
                    style={{ height: "40px" }}
                    onSelect={(event) => {
                      onInventoryFileSelect(event);
                      formik.setFieldValue(
                        "inventoryUpload",
                        event.files[0]?.name || "",
                      );
                    }}
                  />
                  {formik.errors.inventoryUpload &&
                    formik.touched.inventoryUpload && (
                      <div className="text-red-500 text-xs">
                        {formik.errors.inventoryUpload}
                      </div>
                    )}
                  {progressForInventory > 0 && progressForInventory < 100 && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        marginTop: "10px",
                        marginLeft: "5px",
                      }}
                    >
                      <ProgressSpinner
                        style={{ width: "30px", height: "30px" }}
                        strokeWidth="4"
                        fill="transparent"
                        animationDuration=".5s"
                      />
                      <span style={{ whiteSpace: "nowrap" }}>
                        {progressForInventory}% Uploading...
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="cancel-submit-btn mt-8">
            <Button
              label="Submit"
              className="submit-btn ml-2"
              type="submit"
              tabIndex={4}
            />
          </div>
        </form>
      </Dialog>

      <div className="transparant-table">
        <div className="row flex">
          <div className="col-12">
            <InventoryTable
              inventoryItems={filteredItems}
              loading={loadingSpinnerLayout}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryLayout;
